import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Upload,
  Download,
  FileSpreadsheet,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  X,
  Info,
  Building2,
  Users,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const VALID_ROLES = [
  { value: 'owner', label: 'Agency Owner' },
  { value: 'specialist', label: 'Specialist' },
  { value: 'staff', label: 'Staff' },
  { value: 'self_repair_customer', label: 'Self-Repair Customer' },
  { value: 'managed_client', label: 'Managed Client' },
];

const VALID_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const STEPS = [
  { id: 1, label: 'Upload', icon: Upload },
  { id: 2, label: 'Map Columns', icon: FileSpreadsheet },
  { id: 3, label: 'Preview', icon: CheckCircle2 },
  { id: 4, label: 'Import', icon: Users },
];

/**
 * Step indicator component
 */
const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center justify-center gap-2 mb-6">
    {STEPS.map((step, index) => (
      <React.Fragment key={step.id}>
        <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
          currentStep === step.id 
            ? 'bg-amber-100 text-amber-700 border border-amber-200' 
            : currentStep > step.id
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-slate-100 text-slate-400'
        }`}>
          <step.icon size={16} />
          <span className="hidden sm:inline">{step.label}</span>
        </div>
        {index < STEPS.length - 1 && (
          <ChevronRight className="text-slate-300" size={16} />
        )}
      </React.Fragment>
    ))}
  </div>
);

/**
 * Upload Step Component
 */
const UploadStep = ({ onFileSelect, selectedFile, onDownloadTemplate }) => {
  const fileInputRef = useRef(null);
  
  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.csv')) {
      onFileSelect(file);
    } else {
      toast.error('Please upload a CSV file');
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          selectedFile ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-amber-300'
        }`}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        {selectedFile ? (
          <div className="flex items-center justify-center gap-3">
            <CheckCircle2 className="text-emerald-500" size={24} />
            <div className="text-left">
              <p className="font-medium text-slate-900">{selectedFile.name}</p>
              <p className="text-sm text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onFileSelect(null)}
              className="ml-4"
            >
              <X size={16} />
            </Button>
          </div>
        ) : (
          <>
            <Upload className="mx-auto text-slate-400 mb-4" size={40} />
            <p className="font-medium text-slate-700 mb-2">
              Drop your CSV file here, or click to browse
            </p>
            <p className="text-sm text-slate-500 mb-4">
              Supported format: .csv (max 10MB)
            </p>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose File
            </Button>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && onFileSelect(e.target.files[0])}
        />
      </div>
      
      {/* Template Download */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="text-blue-500 mt-0.5" size={18} />
        <div className="flex-1">
          <p className="font-medium text-blue-900">Need a template?</p>
          <p className="text-sm text-blue-700 mt-1">
            Download our CSV template with the correct column headers.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 text-blue-600 border-blue-200 hover:bg-blue-100"
            onClick={onDownloadTemplate}
          >
            <Download size={14} className="mr-2" />
            Download Template
          </Button>
        </div>
      </div>
      
      {/* Required Fields Info */}
      <div className="bg-slate-50 rounded-xl p-4">
        <h4 className="font-medium text-slate-900 mb-3">Required & Optional Fields</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-slate-700 mb-2">Required</p>
            <ul className="space-y-1 text-slate-600">
              <li>• email</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-slate-700 mb-2">Optional</p>
            <ul className="space-y-1 text-slate-600">
              <li>• first_name, last_name</li>
              <li>• phone, extension</li>
              <li>• role, status</li>
              <li>• external_user_id, tags, notes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Column Mapping Step Component
 */
const ColumnMappingStep = ({ headers, mapping, onMappingChange }) => {
  const targetFields = [
    { value: '_skip_', label: 'Do not import' },
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'name', label: 'Full Name' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'extension', label: 'Extension' },
    { value: 'role', label: 'Role' },
    { value: 'status', label: 'Status' },
    { value: 'external_user_id', label: 'External ID' },
    { value: 'tags', label: 'Tags' },
    { value: 'notes', label: 'Notes' },
  ];
  
  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="text-amber-500 mt-0.5" size={18} />
        <div>
          <p className="font-medium text-amber-900">Map your CSV columns</p>
          <p className="text-sm text-amber-700 mt-1">
            Match each column from your file to the correct field. Email is required.
          </p>
        </div>
      </div>
      
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 border-b border-slate-200">
          <p className="text-xs font-semibold text-slate-500 uppercase">CSV Column</p>
          <p className="text-xs font-semibold text-slate-500 uppercase">Map To Field</p>
        </div>
        <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
          {headers.map((header) => (
            <div key={header} className="grid grid-cols-2 gap-4 p-3 items-center">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-xs">
                  {header}
                </Badge>
              </div>
              <Select
                value={mapping[header] || '_skip_'}
                onValueChange={(value) => onMappingChange(header, value === '_skip_' ? '' : value)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select field..." />
                </SelectTrigger>
                <SelectContent>
                  {targetFields.map((field) => (
                    <SelectItem key={field.value} value={field.value}>
                      {field.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Preview Step Component
 */
const PreviewStep = ({ preview, options, onOptionsChange }) => {
  if (!preview) return null;
  
  const getResultBadge = (result) => {
    const styles = {
      ready: 'bg-emerald-100 text-emerald-700',
      warning: 'bg-amber-100 text-amber-700',
      error: 'bg-red-100 text-red-700',
      duplicate: 'bg-slate-100 text-slate-600',
      update_existing: 'bg-blue-100 text-blue-700',
    };
    return styles[result] || styles.ready;
  };
  
  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-3">
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-slate-700">{preview.total_rows}</p>
          <p className="text-xs text-slate-500">Total</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-emerald-700">{preview.valid_rows}</p>
          <p className="text-xs text-emerald-600">Valid</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-amber-700">{preview.warning_rows}</p>
          <p className="text-xs text-amber-600">Warnings</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-red-700">{preview.error_rows}</p>
          <p className="text-xs text-red-600">Errors</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-blue-700">{preview.duplicate_rows}</p>
          <p className="text-xs text-blue-600">Duplicates</p>
        </div>
      </div>
      
      {/* Import Options */}
      <div className="bg-slate-50 rounded-xl p-4 space-y-4">
        <h4 className="font-medium text-slate-900">Import Options</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Default Role
            </label>
            <Select
              value={options.default_role}
              onValueChange={(v) => onOptionsChange({ ...options, default_role: v })}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VALID_ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Default Status
            </label>
            <Select
              value={options.default_status}
              onValueChange={(v) => onOptionsChange({ ...options, default_status: v })}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VALID_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-6 pt-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={options.skip_duplicates}
              onCheckedChange={(v) => onOptionsChange({ ...options, skip_duplicates: v, update_existing: v ? false : options.update_existing })}
            />
            <span className="text-sm text-slate-700">Skip duplicates</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={options.update_existing}
              onCheckedChange={(v) => onOptionsChange({ ...options, update_existing: v, skip_duplicates: v ? false : options.skip_duplicates })}
            />
            <span className="text-sm text-slate-700">Update existing users</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={options.generate_temp_password}
              onCheckedChange={(v) => onOptionsChange({ ...options, generate_temp_password: v })}
            />
            <span className="text-sm text-slate-700">Generate temp password</span>
          </label>
        </div>
      </div>
      
      {/* Preview Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <h4 className="font-medium text-slate-900">Preview Rows</h4>
        </div>
        <div className="overflow-x-auto max-h-[250px]">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Row</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Name</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Email</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Role</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Status</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {preview.rows.slice(0, 50).map((row) => (
                <tr key={row.row_number} className={row.result === 'error' ? 'bg-red-50/50' : ''}>
                  <td className="px-3 py-2 text-slate-500">{row.row_number}</td>
                  <td className="px-3 py-2">{row.first_name} {row.last_name}</td>
                  <td className="px-3 py-2">{row.email}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="text-xs">{row.role}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    <Badge className={row.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                      {row.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <Badge className={getResultBadge(row.result)}>
                      {row.result.replace('_', ' ')}
                    </Badge>
                    {row.errors.length > 0 && (
                      <p className="text-xs text-red-600 mt-1">{row.errors[0]}</p>
                    )}
                    {row.warnings.length > 0 && (
                      <p className="text-xs text-amber-600 mt-1">{row.warnings[0]}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/**
 * Result Step Component
 */
const ResultStep = ({ result, onViewUsers }) => {
  if (!result) return null;
  
  return (
    <div className="space-y-6 text-center py-8">
      <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${
        result.failed === 0 ? 'bg-emerald-100' : 'bg-amber-100'
      }`}>
        {result.failed === 0 ? (
          <CheckCircle2 className="text-emerald-600" size={32} />
        ) : (
          <AlertTriangle className="text-amber-600" size={32} />
        )}
      </div>
      
      <div>
        <h3 className="text-xl font-semibold text-slate-900">
          {result.failed === 0 ? 'Import Complete!' : 'Import Completed with Issues'}
        </h3>
        <p className="text-slate-500 mt-2">
          {result.total_processed} users processed
        </p>
      </div>
      
      <div className="grid grid-cols-4 gap-4 max-w-md mx-auto">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-emerald-700">{result.created}</p>
          <p className="text-xs text-emerald-600">Created</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-blue-700">{result.updated}</p>
          <p className="text-xs text-blue-600">Updated</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-slate-600">{result.skipped}</p>
          <p className="text-xs text-slate-500">Skipped</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-red-700">{result.failed}</p>
          <p className="text-xs text-red-600">Failed</p>
        </div>
      </div>
      
      {result.errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-left max-w-lg mx-auto">
          <p className="font-medium text-red-900 mb-2">Errors:</p>
          <ul className="text-sm text-red-700 space-y-1">
            {result.errors.slice(0, 5).map((err, i) => (
              <li key={i}>• {err.email}: {err.error}</li>
            ))}
          </ul>
        </div>
      )}
      
      <Button onClick={onViewUsers} className="bg-emerald-600 hover:bg-emerald-700">
        <Users size={16} className="mr-2" />
        View Users
      </Button>
    </div>
  );
};

/**
 * ImportUsersModal - Main component
 */
const ImportUsersModal = ({ isOpen, onClose, agencyId, agencyName, onSuccess }) => {
  const { token } = useAuth();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({
    default_role: 'self_repair_customer',
    default_status: 'active',
    skip_duplicates: true,
    update_existing: false,
    generate_temp_password: true,
  });
  
  const headers_config = { Authorization: `Bearer ${token}` };
  
  const handleFileSelect = async (selectedFile) => {
    setFile(selectedFile);
    if (selectedFile) {
      // Parse headers
      const text = await selectedFile.text();
      const lines = text.split('\n');
      if (lines.length > 0) {
        const headerLine = lines[0];
        const parsedHeaders = headerLine.split(',').map(h => h.trim().replace(/"/g, ''));
        setHeaders(parsedHeaders);
        
        // Auto-map columns
        const autoMap = {};
        const headerMapLower = {
          'first_name': ['first_name', 'firstname', 'first'],
          'last_name': ['last_name', 'lastname', 'last'],
          'name': ['name', 'full_name', 'fullname'],
          'email': ['email', 'e-mail', 'emailaddress'],
          'phone': ['phone', 'phone_number', 'telephone'],
          'role': ['role', 'user_role', 'user_type'],
          'status': ['status', 'state', 'active'],
        };
        
        parsedHeaders.forEach(h => {
          const hLower = h.toLowerCase().replace(/[^a-z]/g, '_');
          for (const [field, aliases] of Object.entries(headerMapLower)) {
            if (aliases.includes(hLower)) {
              autoMap[h] = field;
              break;
            }
          }
        });
        
        setMapping(autoMap);
      }
    }
  };
  
  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/admin/users/import/template`, { headers: headers_config });
      const blob = new Blob([res.data.template_csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'user_import_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download template');
    }
  };
  
  const handleMappingChange = (header, field) => {
    setMapping(prev => ({ ...prev, [header]: field }));
  };
  
  const handlePreview = async () => {
    if (!file) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('column_mapping', JSON.stringify(mapping));
      formData.append('default_role', options.default_role);
      formData.append('default_status', options.default_status);
      formData.append('skip_duplicates', options.skip_duplicates);
      formData.append('update_existing', options.update_existing);
      
      const res = await axios.post(
        `${API}/admin/users/import/preview/${agencyId}`,
        formData,
        { headers: { ...headers_config, 'Content-Type': 'multipart/form-data' } }
      );
      
      setPreview(res.data);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview import');
    } finally {
      setLoading(false);
    }
  };
  
  const handleImport = async () => {
    if (!preview) return;
    
    setLoading(true);
    try {
      // Filter to only import valid/warning rows
      const validRows = preview.rows
        .filter(r => ['ready', 'warning', 'update_existing'].includes(r.result))
        .map(r => ({
          first_name: r.first_name,
          last_name: r.last_name,
          email: r.email,
          phone: r.phone,
          role: r.role,
          status: r.status,
        }));
      
      const res = await axios.post(
        `${API}/admin/users/import/execute/${agencyId}`,
        { rows: validRows, options },
        { headers: headers_config }
      );
      
      setResult(res.data);
      setStep(4);
      toast.success(`${res.data.created} users created successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import users');
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    setStep(1);
    setFile(null);
    setHeaders([]);
    setMapping({});
    setPreview(null);
    setResult(null);
    onClose();
    if (result && result.created > 0 && onSuccess) {
      onSuccess();
    }
  };
  
  const canProceed = () => {
    switch (step) {
      case 1: return !!file;
      case 2: return Object.values(mapping).includes('email');
      case 3: return preview && (preview.valid_rows > 0 || preview.update_rows > 0);
      default: return false;
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-users-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="text-amber-500" size={20} />
            Import Users
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <Building2 size={14} className="text-amber-500" />
            Importing into: <strong>{agencyName}</strong>
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <StepIndicator currentStep={step} />
          
          {step === 1 && (
            <UploadStep
              selectedFile={file}
              onFileSelect={handleFileSelect}
              onDownloadTemplate={handleDownloadTemplate}
            />
          )}
          
          {step === 2 && (
            <ColumnMappingStep
              headers={headers}
              mapping={mapping}
              onMappingChange={handleMappingChange}
            />
          )}
          
          {step === 3 && (
            <PreviewStep
              preview={preview}
              options={options}
              onOptionsChange={setOptions}
            />
          )}
          
          {step === 4 && (
            <ResultStep result={result} onViewUsers={handleClose} />
          )}
        </div>
        
        <DialogFooter className="flex justify-between">
          {step < 4 && (
            <>
              <Button
                variant="outline"
                onClick={() => step > 1 ? setStep(step - 1) : handleClose()}
              >
                {step > 1 ? (
                  <>
                    <ChevronLeft size={16} className="mr-1" />
                    Back
                  </>
                ) : 'Cancel'}
              </Button>
              
              <Button
                onClick={() => {
                  if (step === 1) setStep(2);
                  else if (step === 2) handlePreview();
                  else if (step === 3) handleImport();
                }}
                disabled={!canProceed() || loading}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {loading ? (
                  <>
                    <RefreshCw className="animate-spin mr-2" size={16} />
                    Processing...
                  </>
                ) : step === 3 ? (
                  <>
                    <CheckCircle2 size={16} className="mr-2" />
                    Import {preview?.valid_rows || 0} Users
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight size={16} className="ml-1" />
                  </>
                )}
              </Button>
            </>
          )}
          
          {step === 4 && (
            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportUsersModal;
